import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  BooleanInput,
  Form,
  Loading,
  TextInput,
  required,
  useDataProvider,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { AdminDataProvider, Category } from '../dataProvider';

/**
 * Category edit (D-categories-light) — edits a category's OWN fields only: name
 * and the active toggle. The PARENT is shown READ-ONLY; reparenting / tree
 * restructuring is explicitly out of scope (its own deliberate surface later).
 * There is NO create and NO delete for categories.
 *
 * Audit for free (#25): saving calls `dataProvider.update('categories')`; the
 * wrapper records the diff (e.g. status: active → inactive). The route is gated
 * `categories:write`. Categories are market-agnostic — no region guard.
 */

interface CategoryEditValues {
  name: string;
  active: boolean;
}

const ParentField = ({ parentId }: { parentId: number | null }) => {
  const { data: parent } = useGetOne<Category>(
    'categories',
    { id: parentId ?? 0 },
    { enabled: parentId != null },
  );
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Parent category (read-only)
      </Typography>
      <Typography variant="body1">
        {parentId == null ? 'Top-level category' : (parent?.name ?? '…')}
      </Typography>
    </Box>
  );
};

const CategoryEditForm = ({ record }: { record: Category }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const defaultValues: CategoryEditValues = {
    name: record.name,
    active: record.status === 'active',
  };

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as CategoryEditValues;
    const data = {
      name: values.name,
      status: values.active ? 'active' : 'inactive',
    };
    const previousData = { name: record.name, status: record.status };

    try {
      if (JSON.stringify(data) !== JSON.stringify(previousData)) {
        await dataProvider.update('categories', {
          id: record.id,
          data,
          previousData,
        });
      }
      notify('Category updated', { type: 'success' });
      redirect('show', 'categories', record.id);
    } catch {
      notify('Could not save the category', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 480 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Edit {record.name}
      </Typography>
      <Form defaultValues={defaultValues} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextInput
              source="name"
              label="Name"
              validate={required('Name is required')}
              fullWidth
            />
            {/* Parent is read-only — the tree is not edited here. */}
            <ParentField parentId={record.parentId} />
            <BooleanInput source="active" label="Active" />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Save
          </Button>
          <Button
            type="button"
            onClick={() => redirect('show', 'categories', record.id)}
          >
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** The edit view. Exported for testing without the access wrapper. */
export const CategoryEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<Category>('categories', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;
  return <CategoryEditForm record={record} />;
};

/** Gated entry point registered as the `categories` Resource's edit. */
export const CategoryEdit = () => (
  <RequireAccess capability="categories:write">
    <CategoryEditView />
  </RequireAccess>
);
